/**
 * ScreenFlash — full-screen border flash for rare+ events.
 * Renders a fixed overlay with a colored inset box-shadow that fades in/out.
 * Uses only transforms + opacity for GPU-accelerated rendering.
 */

import { useEffect, useState, useCallback } from 'react';
import { eventBus } from '@/services/EventBus';
import { GameEvent, Quality, QualityColors } from '@/types';

const RARE_PLUS: Quality[] = [
  Quality.RARE,
  Quality.EPIC,
  Quality.LEGENDARY,
  Quality.UNIQUE,
];

export function ScreenFlash() {
  const [flash, setFlash] = useState<{ color: string; key: number } | null>(null);

  const triggerFlash = useCallback((color: string) => {
    setFlash({ color, key: Date.now() });
  }, []);

  // Listen to FISH_CAUGHT for rare+ quality
  useEffect(() => {
    const unsub = eventBus.on(GameEvent.FISH_CAUGHT, ({ fish }) => {
      if (RARE_PLUS.includes(fish.quality)) {
        triggerFlash(QualityColors[fish.quality]);
      }
    });
    return unsub;
  }, [triggerFlash]);

  // Auto-clear the flash after animation
  useEffect(() => {
    if (!flash) return;
    const id = setTimeout(() => setFlash(null), 600);
    return () => clearTimeout(id);
  }, [flash]);

  if (!flash) return null;

  return (
    <>
      <style>{flashKeyframes}</style>
      <div
        key={flash.key}
        className="screen-flash"
        style={{
          boxShadow: `inset 0 0 80px 20px ${flash.color}`,
        }}
      />
    </>
  );
}

const flashKeyframes = `
@keyframes screenFlashFade {
  0%   { opacity: 0.7; }
  100% { opacity: 0;   }
}
.screen-flash {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 60;
  animation: screenFlashFade 0.6s ease-out forwards;
}
`;
